import { Component, OnInit, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Firestore,
  doc,
  getDoc,
  collection,
  addDoc,
  serverTimestamp,
  updateDoc,
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';
import { AuthCheckService } from 'src/app/shared/services/auth-check.service';
import { MatDialog } from '@angular/material/dialog';
import { BookingSuccessDialogComponent } from '../../booking-success-dialog/booking-success-dialog.component';
import { BookingLockService } from 'src/app/shared/services/booking-lock.service';
import { CartService, CartItem } from 'src/app/shared/services/cart.service';
import { firstValueFrom } from 'rxjs';
import { NotificationService } from 'src/app/shared/services/notification.service';

@Component({
  selector: 'app-confirm-enquiry',
  templateUrl: './confirm-enquiry.component.html',
  styleUrls: ['./confirm-enquiry.component.scss'],
})
export class ConfirmEnquiryComponent implements OnInit {
  isHeaderScrolled = false;

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const scrollOffset = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    this.isHeaderScrolled = scrollOffset > 10;
  }

  cartItems: CartItem[] = [];
  isBookingSuccess = false;

  isEditing = false;
  userDetails = {
    name: '',
    phone: '',
    email: '',
    companyName: '',
    businessLicenseNo: '',
    licenseValidTill: null as Date | null,
    licenseIssuedBy: '',
    companyAddress: '',
    isIndividual: false,
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private firestore: Firestore,
    private ux: UxserviceService,
    private authCheck: AuthCheckService,
    private dialog: MatDialog,
    private bookingLock: BookingLockService,
    private cartService: CartService,
    private notificationService: NotificationService,
    private auth: Auth,
  ) { }

  async ngOnInit() {
    this.ux.showOverlay('Loading details...');
    
    // 1. Load User Details First
    await this.loadUserDetails();

    // 2. Safety Check: If logged in but missing business details -> redirect
    if (this.auth.currentUser && (this.userDetails.isIndividual || !this.userDetails.companyName)) {
      this.router.navigate(['/setup-role'], {
        queryParams: { 
          type: 'customer', 
          returnUrl: this.router.url 
        }
      });
      return;
    }

    // 3. Load Cart/Item Data
    this.route.paramMap.subscribe(async (params) => {
      const id = params.get('id');

      if (id === 'cart') {
        this.cartService.cartItems$.subscribe((items) => {
          this.cartItems = items;
          
          // Only show the "empty cart" error if:
          // 1. The cart truly is empty
          // 2. We aren't in the middle of a successful checkout redirect
          // 3. The user IS still logged in (prevents toast on logout)
          if (this.cartItems.length === 0 && !this.isBookingSuccess && this.auth.currentUser) {
            this.ux.showError('Your cart is empty');
            this.router.navigate(['/']);
          }
        });
      } else if (id) {
        this.ux.showError('Please use the cart to confirm bookings.');
        this.router.navigate(['/cart']);
      }

      this.ux.instantCloseOverlay();
    });
  }


  private async loadUserDetails() {
    const user = this.auth.currentUser;
    if (!user) return;

    const uid = user.uid;
    let name = user.displayName || '';
    let email = user.email || '';
    let phone = '';
    let companyName = '';
    let businessLicenseNo = '';
    let licenseValidTill: Date | null = null;
    let licenseIssuedBy = '';
    let companyAddress = '';
    let isIndividual = false;

    const userRef = doc(this.firestore, `users-list/${uid}`);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      const data: any = userSnap.data();
      name = data.firstname || name || 'User';
      email = data.email || email || '';
      phone = data.phoneNumber || data.phone || '';
      companyName = data.companyName || '';
      companyAddress = data.companyAddress || '';
      isIndividual = data.isIndividual || false;

      // Load business fields
      businessLicenseNo = data.businessLicenseNo || '';
      licenseIssuedBy = data.licenseIssuedBy || '';

      if (data.licenseValidTill) {
        if (data.licenseValidTill.toDate) {
          licenseValidTill = data.licenseValidTill.toDate();
        } else if (data.licenseValidTill instanceof Date) {
          licenseValidTill = data.licenseValidTill;
        } else if (typeof data.licenseValidTill === 'string') {
          licenseValidTill = new Date(data.licenseValidTill);
        }
      }
    }

    this.userDetails = {
      name,
      phone,
      email,
      companyName,
      businessLicenseNo,
      licenseValidTill,
      licenseIssuedBy,
      companyAddress,
      isIndividual,
    };
  }

  get grandTotal(): number {
    return this.cartItems.reduce((acc, item) => acc + item.totalAmount, 0);
  }

  get finalAmount(): number {
    return this.grandTotal;
  }

  async onConfirm() {
    // 1. Authentication Check
    const user = this.auth.currentUser;
    if (!user) {
      this.ux.showError('Please login to confirm your enquiry.');
      this.router.navigate(['/login'], {
        queryParams: { returnUrl: this.router.url }
      });
      return;
    }

    if (this.cartItems.length === 0) return;

    // 2. Business Details Validation
    if (
      this.userDetails.isIndividual ||
      !this.userDetails.name ||
      !this.userDetails.phone ||
      !this.userDetails.email ||
      !this.userDetails.companyName ||
      !this.userDetails.businessLicenseNo ||
      !this.userDetails.licenseIssuedBy ||
      !this.userDetails.licenseValidTill
    ) {
      this.router.navigate(['/setup-role'], {
        queryParams: { 
          type: 'customer', 
          returnUrl: this.router.url 
        }
      });
      return;
    }

    this.ux.showOverlay('Processing your booking...');

    // 🔥 1. Auto-save Contact Details to Profile (Sync)
    try {
      const currentUserProfile = this.authCheck['auth']?.currentUser;
      if (currentUserProfile) {
        const userRef = doc(this.firestore, `users-list/${currentUserProfile.uid}`);
        const updateData: any = {
          firstname: this.userDetails.name,
          phone: this.userDetails.phone,
          email: this.userDetails.email,
          companyName: this.userDetails.companyName || '',
          businessLicenseNo: this.userDetails.businessLicenseNo || '',
          licenseIssuedBy: this.userDetails.licenseIssuedBy || '',
          companyAddress: this.userDetails.companyAddress || '',
        };

        if (this.userDetails.licenseValidTill) {
          updateData.licenseValidTill = this.userDetails.licenseValidTill;
        }

        // Silent update
        await updateDoc(userRef, updateData);
        console.log('Profile auto-synced with enquiry details');
      }
    } catch (error) {
      console.warn('Failed to auto-sync profile details:', error);
      // Proceed with booking anyway
    }

    // 🔥 2. Proceed with Booking Creation
    const userId = user?.uid || '';
    const userName =
      this.userDetails.name ||
      user?.displayName ||
      user?.email ||
      'Unknown User';

    const bookingsCreated: string[] = [];
    const errors: string[] = [];

    // Generate Single Master Booking ID for the entire order
    const masterBookingId = this.generateBookingId();
    // Track vendor-specific sub-IDs: VendorID -> GeneratedSubID
    const vendorBookingIdMap = new Map<string, string>();
    let vendorCounter = 1;

    // Loop through each item to process booking
    for (const item of this.cartItems) {
      try {
        // 1. Check Lock (FCFS)
        const isLocked = await this.bookingLock.checkAndLockVehicle(
          item.equipmentId,
          new Date(item.startDate),
          new Date(item.endDate),
          userId,
        );

        if (!isLocked) {
          errors.push(
            `Vehicle ${item.equipmentData.name} is not available for selected dates.`,
          );
          continue; // Skip this item
        }

        // 2. Prepare IDs
        const vendorId = item.vendorId || item.equipmentData.bywhom;

        // Generate or retrieve Vendor Booking ID
        let vendorBookingId = '';
        if (vendorId) {
          if (!vendorBookingIdMap.has(vendorId)) {
            // Create a sub-ID, e.g., ALU-1234-1
            const subId = `${masterBookingId}-${vendorCounter++}`;
            vendorBookingIdMap.set(vendorId, subId);
          }
          vendorBookingId = vendorBookingIdMap.get(vendorId) || '';
        }

        const bookingRef = collection(this.firestore, 'booking-list');

        const docRef = await addDoc(bookingRef, {
          bookingId: masterBookingId, // Shared Master ID
          vendorBookingId: vendorBookingId, // New Field: Vendor Specific ID
          customerId: userId,
          vendorId: vendorId,
          startDate: new Date(item.startDate),
          endDate: new Date(item.endDate),
          location: item.address || '',

          // Flattened booking details
          equipmentId: item.equipmentId,

          bywhom: userId, // Keeping for potential legacy queries
          bywhomname: userName,

          priceType: item.priceType,
          rate: item.rate,
          totalAmount: item.totalAmount,
          createdAt: serverTimestamp(),
          status: 'Pending',
        });

        bookingsCreated.push(masterBookingId);

        // 3. Send Notification to Vendor
        if (vendorId) {
          await this.notificationService.sendNotification(vendorId, {
            title: 'New Booking Request',
            message: `New booking for ${item.equipmentData.name}`,
            bookingId: docRef.id, // Links to specific document
            type: 'info',
            link: `/business/booking/booking-details/${docRef.id}`,
          });
        }
      } catch (err) {
        console.error('Error processing item:', item.equipmentId, err);
        errors.push(`Failed to book ${item.equipmentData.name}.`);
      }
    }

    this.ux.instantCloseOverlay();

    if (bookingsCreated.length > 0) {
      // Flag to prevent "Cart Empty" error from redirecting
      this.isBookingSuccess = true;
      // Clear Cart
      this.cartService.clearCart();

      // Show Success Dialog
      if (errors.length > 0) {
        this.ux.showError(`Some items failed: ${errors.join(', ')}`);
      }

      // Deduplicate booking IDs for display
      const uniqueIds = [...new Set(bookingsCreated)];

      this.dialog.open(BookingSuccessDialogComponent, {
        panelClass: 'success-dialog-panel',
        backdropClass: 'blurred-backdrop',
        disableClose: true,
        data: { bookingIds: uniqueIds },
      });
    } else {
      this.ux.showError(`Booking failed: ${errors.join(' ')}`);
    }
  }

  cancel() {
    this.router.navigate(['/cart']);
  }

  enableEdit() {
    this.isEditing = true;
  }

  async saveData() {
    if (
      !this.userDetails.name ||
      !this.userDetails.phone ||
      !this.userDetails.email ||
      !this.userDetails.companyName ||
      !this.userDetails.businessLicenseNo ||
      !this.userDetails.licenseIssuedBy ||
      !this.userDetails.licenseValidTill
    ) {
      this.ux.showError(
        'Please fill in all required fields including Company Name and License Details.',
      );
      return;
    }

    this.ux.showOverlay('Saving your details...');
    try {
      const user = this.authCheck['auth']?.currentUser;
      if (!user) {
        this.ux.instantCloseOverlay();
        return;
      }

      const userRef = doc(this.firestore, `users-list/${user.uid}`);

      const updateData: any = {
        firstname: this.userDetails.name,
        phone: this.userDetails.phone,
        email: this.userDetails.email,
        companyName: this.userDetails.companyName || '', // Added companyName here
        businessLicenseNo: this.userDetails.businessLicenseNo || '',
        licenseIssuedBy: this.userDetails.licenseIssuedBy || '',
        companyAddress: this.userDetails.companyAddress || '',
      };

      if (this.userDetails.licenseValidTill) {
        updateData.licenseValidTill = this.userDetails.licenseValidTill;
      }

      await updateDoc(userRef, updateData);

      this.ux.instantCloseOverlay();
      this.ux.showSuccess('Contact details saved successfully.');
      this.isEditing = false;
    } catch (error) {
      console.error('Error saving user details:', error);
      this.ux.instantCloseOverlay();
      this.ux.showError('Failed to save details. Please try again.');
    }
  }

  closeDialog() {
    this.dialog.closeAll();
  }

  generateBookingId(): string {
    const prefix = 'ALU-';
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    return prefix + randomNum;
  }
}
